import { Alpine } from '../core/alpine-core.js';
import { apiFetch } from './api.js';
import { CURRENT_SEASON } from '../core/current-season.js';
import { effectiveAdminLeagueId, getLeagueIdFromPath, stripLeagueFromAdminPath, withLeagueId } from './league-context.js';
import {
  downloadBlob,
  parseImportDocument,
  pickTextFile,
  sanitizeExportBasename,
  textExportWithJsonSection,
  wrapExportDocument,
} from './league-file-io.js';
import {
  buildLeagueBuilderTemplateSummaryLines,
  leagueBuilderTemplateExportPayload,
} from './league-rules-summary.js';

const LEAGUE_BUILDER_TEMPLATE_KIND = 'dartsos.league-builder-template.v1';

const LEAGUE_KEY_MAX_LEN = 6;
const DEFAULT_TEMPLATES_URL = '/assets/league-builder/default-templates.json';

const WEEKDAYS = [
  { id: 0, key: 'sun', label: 'Sunday' },
  { id: 1, key: 'mon', label: 'Monday' },
  { id: 2, key: 'tue', label: 'Tuesday' },
  { id: 3, key: 'wed', label: 'Wednesday' },
  { id: 4, key: 'thu', label: 'Thursday' },
  { id: 5, key: 'fri', label: 'Friday' },
  { id: 6, key: 'sat', label: 'Saturday' },
];

function weekdayIdFromKey(key) {
  const row = WEEKDAYS.find((d) => d.key === key);
  return row ? row.id : 4;
}

function nextId(prefix = 'row') {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function dateIsoOnly(date) {
  return date.toISOString().slice(0, 10);
}

function combineDateTimeToIso(dateStr, timeStr) {
  const t = String(timeStr || '19:30').trim() || '19:30';
  return new Date(`${dateStr}T${t}:00+07:00`).toISOString();
}

function parseDateOnly(dateStr) {
  return new Date(`${dateStr}T00:00:00`);
}

function toDateOnly(d) {
  return dateIsoOnly(d);
}

function mondayKey(dateStr) {
  const d = parseDateOnly(dateStr);
  const day = d.getDay();
  const delta = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + delta);
  return toDateOnly(d);
}

function weekdayLabelFromIso(dateStr) {
  return parseDateOnly(dateStr).toLocaleDateString('en-GB', { weekday: 'short' });
}

function dateLabelFromIso(dateStr) {
  return parseDateOnly(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function rotateRoundRobin(teamIds) {
  const list = [...teamIds];
  if (list.length % 2 !== 0) list.push(null);
  const rounds = list.length - 1;
  const half = list.length / 2;
  const rows = [];
  let arr = [...list];
  for (let r = 0; r < rounds; r += 1) {
    const matches = [];
    for (let i = 0; i < half; i += 1) {
      const a = arr[i];
      const b = arr[arr.length - 1 - i];
      if (a && b) {
        if (r % 2 === 0) matches.push([a, b]);
        else matches.push([b, a]);
      }
    }
    rows.push(matches);
    const fixed = arr[0];
    const rest = arr.slice(1);
    rest.unshift(rest.pop());
    arr = [fixed, ...rest];
  }
  return rows;
}

function upsertUsedVenue(map, date, venueId) {
  if (!venueId) return;
  const bucket = map.get(date) || new Set();
  bucket.add(String(venueId));
  map.set(date, bucket);
}

function sanitizeLeagueKey(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, LEAGUE_KEY_MAX_LEN);
}

function randomLeagueKeyCandidate(len = 4) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const n = Math.max(2, Math.min(LEAGUE_KEY_MAX_LEN, Number(len) || 4));
  let out = '';
  for (let i = 0; i < n; i += 1) {
    out += letters[Math.floor(Math.random() * letters.length)];
  }
  return out;
}

function isFixturesBuilderAdminPath(pathname = globalThis.location?.pathname || '') {
  const adminPath = stripLeagueFromAdminPath(pathname);
  return adminPath === '/admin/fixtures-builder' || adminPath.startsWith('/admin/fixtures-builder/');
}

Alpine.data('leagueBuilderPage', () => ({
  fixturesBuilderMode: false,
  step: 1,
  totalSteps: 4,
  loading: true,
  generating: false,
  saving: false,
  error: '',

  form: {
    leagueKey: '',
    leagueName: '',
    season: CURRENT_SEASON,
    title: '',
    templateName: '',
    templateSeasonTarget: '',
    leagueRulesId: '',
    startDate: dateIsoOnly(new Date()),
    startTime: '19:30',
    frequency: 'weekly', // weekly | twice
    weekday1: 'thu',
    weekday2: 'tue',
    roundRobinCycles: 2,
    teamMode: 'manual', // manual | auto
    balanceHomeAway: true,
    avoidVenueClashes: true,
  },

  leagueRulesOptions: [],
  selectedRule: null,
  teams: [],
  venues: [],
  selectedTeamIds: [],
  teamSearch: { q: '' },
  teamsLoading: false,
  generatedRows: [],
  customRows: [],
  planRows: [],
  templates: [],
  templateSelectedId: '',
  templateLoading: false,
  templateSaving: false,
  templateDeleting: false,
  templateImporting: false,
  templateInfoOpen: false,
  planWarnings: [],
  usedLeagueKeys: [],

  async init() {
    this.fixturesBuilderMode = isFixturesBuilderAdminPath();
    await this.loadUsedLeagueKeys();
    await this.loadDependencies();
    if (this.fixturesBuilderMode) {
      await this.bootstrapFixturesBuilder();
    }
    await this.loadTemplates();
    this.form.templateSeasonTarget = this.form.season;
    await this.loadTeams();
    if (this.fixturesBuilderMode && this.selectedTeamIds.length < 2) {
      this.selectedTeamIds = this.teams
        .filter((t) => this.teamLeagueLabel(t) === sanitizeLeagueKey(this.form.leagueKey))
        .map((t) => String(t._id));
    }
    this.loading = false;
  },

  pageTitle() {
    return this.fixturesBuilderMode ? 'Fixtures Builder' : 'League Builder';
  },

  pageSubtitle() {
    return this.fixturesBuilderMode
      ? 'Generate fixtures, events, and breaks for the current league — league ID and name are fixed.'
      : 'Multi-step setup for league fixtures, events, breaks and holidays with league rules attached.';
  },

  basicsStepTitle() {
    return this.fixturesBuilderMode ? '1) League context' : '1) League Basics';
  },

  fixturesListHref() {
    return withLeagueId('/admin/fixtures', this.form.leagueKey);
  },

  async bootstrapFixturesBuilder() {
    const leagueKey = getLeagueIdFromPath() || effectiveAdminLeagueId('PDL');
    const clean = sanitizeLeagueKey(leagueKey);
    if (!clean) {
      this.$store.toastr.error('Open Fixtures Builder from a league fixtures page.');
      return;
    }
    this.form.leagueKey = clean;
    let leaguePayload = null;
    try {
      const res = await apiFetch(`/leagues/${encodeURIComponent(clean)}?season=${encodeURIComponent(CURRENT_SEASON)}`);
      const data = await res.json().catch(() => ({}));
      leaguePayload = data?.league || null;
      if (res.ok && leaguePayload) {
        this.form.leagueName = String(leaguePayload.name || clean);
        this.form.season = String(leaguePayload.season || CURRENT_SEASON);
      } else {
        this.form.leagueName = clean;
        this.form.season = CURRENT_SEASON;
      }
    } catch {
      this.form.leagueName = clean;
      this.form.season = CURRENT_SEASON;
    }
    const rule = leaguePayload?.leagueRulesId
      ? this.leagueRulesOptions.find((r) => String(r._id) === String(leaguePayload.leagueRulesId))
      : null;
    if (rule) {
      this.form.leagueRulesId = String(rule._id);
      this.selectedRule = rule;
      this.applyScheduleFromSelectedRule();
    } else {
      const legacy = this.leagueRulesOptions.find((r) => sanitizeLeagueKey(r.leagueKey) === clean);
      if (legacy) {
        this.form.leagueRulesId = String(legacy._id);
        this.selectedRule = legacy;
        this.applyScheduleFromSelectedRule();
      }
    }
  },

  async loadUsedLeagueKeys() {
    try {
      const res = await apiFetch('/league-builder/used-league-keys');
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) throw new Error(data?.message || 'Failed to load league IDs');
      this.usedLeagueKeys = Array.isArray(data.items) ? data.items.map((k) => sanitizeLeagueKey(k)).filter(Boolean) : [];
    } catch {
      this.usedLeagueKeys = [];
    }
  },

  sanitizeLeagueKey(value) {
    return sanitizeLeagueKey(value);
  },

  leagueKeyInUse(key = this.form.leagueKey) {
    const k = sanitizeLeagueKey(key);
    if (!k) return false;
    if (this.fixturesBuilderMode && k === sanitizeLeagueKey(this.form.leagueKey)) return false;
    return this.usedLeagueKeys.includes(k);
  },

  leagueNameTrimmed() {
    return String(this.form.leagueName || '').trim();
  },

  leagueDisplayLabel() {
    return this.leagueNameTrimmed() || sanitizeLeagueKey(this.form.leagueKey) || 'your new league';
  },

  validateBasicsStep() {
    if (this.fixturesBuilderMode) {
      const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
      if (!leagueKey) {
        this.$store.toastr.warning('League context missing — open Fixtures Builder from a league.');
        return false;
      }
      if (!String(this.form.season || '').trim()) {
        this.$store.toastr.warning('Enter a season.');
        return false;
      }
      return true;
    }
    if (!this.leagueNameTrimmed()) {
      this.$store.toastr.warning('Enter a league name on step 1.');
      return false;
    }
    const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
    if (!leagueKey || leagueKey.length < 2) {
      this.$store.toastr.warning('Enter a league ID (2–6 uppercase letters) on step 1.');
      return false;
    }
    if (this.leagueKeyInUse(leagueKey)) {
      this.$store.toastr.warning('Choose a unique league ID before continuing.');
      return false;
    }
    return true;
  },

  leagueRulesForPicker() {
    return this.leagueRulesOptions;
  },

  async saveLeagueProfile() {
    const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
    const name = this.leagueNameTrimmed();
    const season = String(this.form.season || '').trim();
    if (!leagueKey || !name) return;
    const res = await apiFetch(`/admin/leagues/${encodeURIComponent(leagueKey)}`, {
      method: 'PATCH',
      body: {
        name,
        season,
        leagueRulesId: String(this.form.leagueRulesId || '').trim(),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) {
      throw new Error(data?.message || 'Failed to save league profile');
    }
  },

  onLeagueKeyInput() {
    if (this.fixturesBuilderMode) return;
    const next = sanitizeLeagueKey(this.form.leagueKey);
    if (next !== this.form.leagueKey) this.form.leagueKey = next;
    if (this._leagueKeyTimer) clearTimeout(this._leagueKeyTimer);
    this._leagueKeyTimer = setTimeout(() => {
      this.loadTeams();
      this.loadTemplates();
    }, 250);
  },

  async generateLeagueKey() {
    await this.loadUsedLeagueKeys();
    for (let attempt = 0; attempt < 240; attempt += 1) {
      const len = 4 + (attempt % 3);
      const candidate = randomLeagueKeyCandidate(len);
      if (!this.usedLeagueKeys.includes(candidate)) {
        this.form.leagueKey = candidate;
        await this.loadTeams();
        await this.loadTemplates();
        this.$store.toastr.success(`League ID ${candidate} generated`);
        return;
      }
    }
    this.$store.toastr.error('Could not generate a unique league ID — try entering one manually.');
  },

  applyScheduleFromSelectedRule() {
    const rule = this.selectedRule;
    if (!rule) return;
    if (rule.matchStartTime) {
      this.form.startTime = String(rule.matchStartTime);
    }
    const cadence = String(rule.fixtureCadence || '');
    if (cadence === 'twice_weekly') {
      this.form.frequency = 'twice';
      if (rule.fixtureWeekday) this.form.weekday1 = String(rule.fixtureWeekday);
      if (rule.fixtureWeekday2) this.form.weekday2 = String(rule.fixtureWeekday2);
    } else {
      this.form.frequency = 'weekly';
      if (rule.fixtureWeekday) this.form.weekday1 = String(rule.fixtureWeekday);
    }
  },

  scheduleRulesHint() {
    if (!this.selectedRule) return 'Select a league rules profile on step 1 — schedule fields will prefill from it.';
    const cadence = String(this.selectedRule.fixtureCadence || 'weekly');
    const d1 = String(this.selectedRule.fixtureWeekday || this.form.weekday1 || 'thu');
    const d2 = String(this.selectedRule.fixtureWeekday2 || this.form.weekday2 || 'tue');
    if (cadence === 'twice_weekly') return `Prefilled from rules: twice weekly (${d1} + ${d2}). Change matchdays or start date below.`;
    return `Prefilled from rules: ${cadence.replace(/_/g, ' ')} on ${d1}. Change matchday or start date below.`;
  },

  async loadDependencies() {
    const [rulesRes, venuesRes] = await Promise.all([
      apiFetch('/league-rules?limit=200'),
      apiFetch('/venues?active=true&limit=200'),
    ]);
    const [rulesJson, venuesJson] = await Promise.all([rulesRes.json(), venuesRes.json()]);
    this.leagueRulesOptions = Array.isArray(rulesJson?.items) ? rulesJson.items : [];
    this.venues = Array.isArray(venuesJson?.items) ? venuesJson.items : [];
  },

  async loadBuiltinTemplates() {
    try {
      const res = await fetch(DEFAULT_TEMPLATES_URL, { cache: 'no-store' });
      if (!res.ok) return [];
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data?.templates) ? data.templates : [];
      return rows.map((tpl) => ({
        ...tpl,
        _id: String(tpl._id || `default:${tpl.name || 'template'}`),
        builtin: tpl.builtin !== false,
        season: String(tpl.season || this.form.season || CURRENT_SEASON),
        payload: { ...(tpl.payload || {}) },
      }));
    } catch {
      return [];
    }
  },

  async loadTeams() {
    this.teamsLoading = true;
    try {
      const q = new URLSearchParams({
        active: 'true',
        limit: '400',
        sortBy: 'name:asc',
      });
      const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
      if (this.fixturesBuilderMode && leagueKey) {
        q.set('league', leagueKey);
      }
      const res = await apiFetch(`/teams?${q.toString()}`);
      const data = await res.json();
      this.teams = Array.isArray(data?.items) ? data.items : [];
      const validIds = new Set(this.teams.map((t) => String(t._id)));
      this.selectedTeamIds = this.selectedTeamIds.filter((id) => validIds.has(String(id)));
      if (this.form.teamMode === 'auto') {
        this.selectedTeamIds = this.teams.map((t) => String(t._id));
      }
    } catch (e) {
      this.teams = [];
      this.$store.toastr.error(e?.message || 'Failed to load teams');
    } finally {
      this.teamsLoading = false;
      this.rebuildPlanRows();
    }
  },

  get teamsForPicker() {
    const needle = String(this.teamSearch?.q || '').trim().toLowerCase();
    let list = Array.isArray(this.teams) ? [...this.teams] : [];
    if (needle) {
      list = list.filter((t) => {
        const hay = [
          t.name,
          t.league,
          t.season,
          t.venue?.name,
          t.captainPlayerId?.fullName,
          t.captainPlayerId?.nickname,
        ]
          .map((s) => String(s || '').toLowerCase())
          .join(' ');
        return hay.includes(needle);
      });
    }
    list.sort((a, b) => {
      const aSel = this.isTeamSelected(a) ? 0 : 1;
      const bSel = this.isTeamSelected(b) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
    return list;
  },

  isTeamSelected(team) {
    return this.selectedTeamIds.includes(String(team?._id));
  },

  teamLeagueLabel(team) {
    const raw = String(team?.league || team?.leagueId || '').trim();
    if (!raw) return '';
    return raw.toUpperCase();
  },

  teamVenueLabel(team) {
    return String(team?.venue?.name || '').trim() || 'No home venue';
  },

  teamJoinHint(team) {
    const target = sanitizeLeagueKey(this.form.leagueKey);
    const current = this.teamLeagueLabel(team);
    if (!target) return current ? `Currently in ${current}` : 'Account team — not in a league yet';
    if (!current) return `Not in a league yet — joins ${target} when fixtures are created`;
    if (current === target) return `Already in ${target}`;
    return `In ${current} — will join ${target} when fixtures are created`;
  },

  selectAllTeams() {
    this.form.teamMode = 'manual';
    const ids = this.teamsForPicker.map((t) => String(t._id));
    const merged = new Set([...this.selectedTeamIds.map(String), ...ids]);
    this.selectedTeamIds = [...merged];
  },

  clearTeamSelection() {
    this.form.teamMode = 'manual';
    this.selectedTeamIds = [];
  },

  async assignSelectedTeamsToLeague() {
    const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
    const season = String(this.form.season || '').trim();
    if (!leagueKey || !this.selectedTeamIds.length) return;
    for (const id of this.selectedTeamIds) {
      const res = await apiFetch(`/teams/${encodeURIComponent(String(id))}`, {
        method: 'PATCH',
        body: { league: leagueKey, season },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.message || `Failed to assign team ${id} to ${leagueKey}`);
      }
    }
  },

  async setRule(ruleId) {
    this.form.leagueRulesId = ruleId;
    this.selectedRule = this.leagueRulesOptions.find((r) => String(r._id) === String(ruleId)) || null;
    if (this.selectedRule) {
      if (!this.leagueNameTrimmed() && this.selectedRule.displayName) {
        this.form.leagueName = String(this.selectedRule.displayName).trim();
      }
      this.applyScheduleFromSelectedRule();
      await this.loadTeams();
      await this.loadTemplates();
    }
  },

  async loadTemplates() {
    this.templateLoading = true;
    try {
      const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
      const q = new URLSearchParams();
      if (leagueKey) q.set('league', leagueKey);
      const [builtin, savedRes] = await Promise.all([
        this.loadBuiltinTemplates(),
        apiFetch(`/league-builder-templates?${q.toString()}`),
      ]);
      const savedJson = await savedRes.json().catch(() => ({}));
      if (!savedRes.ok || savedJson?.ok === false) throw new Error(savedJson?.message || 'Failed to load templates');
      const saved = Array.isArray(savedJson.items) ? savedJson.items : [];
      const seen = new Set();
      this.templates = [...builtin, ...saved].filter((tpl) => {
        const id = String(tpl._id || '');
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    } catch (e) {
      this.templates = await this.loadBuiltinTemplates();
      this.$store.toastr.error(e.message || 'Failed to load templates');
    } finally {
      this.templateLoading = false;
    }
  },

  currentTemplate() {
    return this.templates.find((t) => String(t._id) === String(this.templateSelectedId)) || null;
  },

  canExportSelectedTemplate() {
    return !!String(this.templateSelectedId || '').trim() && !!this.currentTemplate();
  },

  templateLabel(t) {
    const suffix = t.builtin ? ' · default' : (t.season ? ` · ${t.season}` : '');
    return `${t.name}${suffix}`;
  },

  applyTemplateToTargetSeason() {
    const target = String(this.form.templateSeasonTarget || '').trim();
    if (!target) {
      this.$store.toastr.warning('Enter target season');
      return;
    }
    this.form.season = target;
    this.$store.toastr.success(`Template settings applied to season ${target}`);
  },

  templatePayload() {
    const payload = {
      leagueName: this.leagueNameTrimmed(),
      season: this.form.season,
      leagueRulesId: this.form.leagueRulesId,
      startDate: this.form.startDate,
      startTime: this.form.startTime,
      frequency: this.form.frequency,
      weekday1: this.form.weekday1,
      weekday2: this.form.weekday2,
      roundRobinCycles: this.form.roundRobinCycles,
      teamMode: this.form.teamMode,
      selectedTeamIds: this.selectedTeamIds.map(String),
      customRows: this.customRows.map((row) => ({
        type: row.type,
        date: row.date,
        title: row.title || '',
        venueId: row.venueId || '',
        homeTeamId: row.homeTeamId || '',
        awayTeamId: row.awayTeamId || '',
      })),
      balanceHomeAway: !!this.form.balanceHomeAway,
      avoidVenueClashes: !!this.form.avoidVenueClashes,
    };
    if (!this.fixturesBuilderMode) {
      payload.leagueKey = sanitizeLeagueKey(this.form.leagueKey);
    }
    return payload;
  },

  templateExportDocument(extra = {}) {
    const tpl = this.currentTemplate();
    if (!tpl) throw new Error('Select a template first');
    const storedPayload = tpl.payload && typeof tpl.payload === 'object' ? tpl.payload : this.templatePayload();
    return wrapExportDocument(
      LEAGUE_BUILDER_TEMPLATE_KIND,
      leagueBuilderTemplateExportPayload(
        {
          ...this.form,
          templateName: tpl.name,
          leagueKey: tpl.leagueKey || storedPayload.leagueKey || this.form.leagueKey,
          season: tpl.season || storedPayload.season || this.form.season,
        },
        storedPayload,
        {
          name: extra.name || tpl.name || 'League template',
          description: extra.description || tpl.description || '',
          leagueKey: this.fixturesBuilderMode
            ? undefined
            : sanitizeLeagueKey(tpl.leagueKey || storedPayload.leagueKey || this.form.leagueKey),
          season: tpl.season || storedPayload.season || this.form.season,
        },
      ),
    );
  },

  templateSummaryContext(tpl = this.currentTemplate()) {
    const rulesId = tpl?.payload?.leagueRulesId || this.form.leagueRulesId;
    const rulesName = this.leagueRulesOptions.find((r) => String(r._id) === String(rulesId))?.displayName || '';
    return { rulesName };
  },

  selectedTemplateSummaryLines(tpl = this.currentTemplate()) {
    if (!tpl) return [];
    return buildLeagueBuilderTemplateSummaryLines(tpl, this.templateSummaryContext(tpl));
  },

  openTemplateInfoDialog() {
    const tpl = this.currentTemplate();
    if (!tpl) {
      this.$store.toastr.warning('Select a template first');
      return;
    }
    this.templateInfoOpen = true;
  },

  closeTemplateInfoDialog() {
    this.templateInfoOpen = false;
  },

  exportTemplateJson() {
    if (!this.canExportSelectedTemplate()) {
      this.$store.toastr.warning('Select a template first');
      return;
    }
    try {
      const doc = this.templateExportDocument();
      const base = sanitizeExportBasename(doc.name, 'league-template');
      downloadBlob(`${base}.json`, JSON.stringify(doc, null, 2), 'application/json;charset=utf-8');
      this.$store.toastr.success('Template exported as JSON');
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Export failed');
    }
  },

  exportTemplateTxt() {
    if (!this.canExportSelectedTemplate()) {
      this.$store.toastr.warning('Select a template first');
      return;
    }
    try {
      const doc = this.templateExportDocument();
      const summary = buildLeagueBuilderTemplateSummaryLines(
        { name: doc.name, description: doc.description, season: doc.season, payload: doc.payload, builtin: false },
        this.templateSummaryContext({ payload: doc.payload }),
      );
      const text = textExportWithJsonSection(`League builder template — ${doc.name}`, summary, doc);
      const base = sanitizeExportBasename(doc.name, 'league-template');
      downloadBlob(`${base}.txt`, text);
      this.$store.toastr.success('Template exported as text');
    } catch (e) {
      this.$store.toastr.error(e?.message || 'Export failed');
    }
  },

  async importTemplateFile() {
    this.templateImporting = true;
    try {
      const picked = await pickTextFile('.json,.txt,application/json,text/plain');
      if (!picked) return;
      const doc = parseImportDocument(picked.text, LEAGUE_BUILDER_TEMPLATE_KIND);
      await this.applyImportedTemplateDoc(doc);
      this.$store.toastr.success(`Imported template "${doc.name || picked.file.name}"`);
    } catch (e) {
      this.$store.toastr.error(e.message || 'Import failed');
    } finally {
      this.templateImporting = false;
    }
  },

  async applyImportedTemplateDoc(doc) {
    const payload = doc.payload || {};
    const virtualTpl = {
      _id: `import:${Date.now()}`,
      name: String(doc.name || 'Imported template').trim(),
      description: String(doc.description || '').trim(),
      season: String(doc.season || payload.season || this.form.season),
      payload,
      builtin: false,
    };
    this.templateSelectedId = '';
    await this.applyTemplatePayload(virtualTpl, { fromImport: true });
    this.form.templateName = virtualTpl.name;
  },

  async applySelectedTemplate() {
    const tpl = this.currentTemplate();
    if (!tpl) {
      this.$store.toastr.warning('Select a template first');
      return;
    }
    await this.applyTemplatePayload(tpl);
    this.$store.toastr.success(`Loaded template "${tpl.name}"`);
  },

  async applyTemplatePayload(tpl, opts = {}) {
    const payload = tpl.payload || {};
    if (!this.fixturesBuilderMode && payload.leagueKey) {
      this.form.leagueKey = sanitizeLeagueKey(payload.leagueKey);
    } else if (!this.fixturesBuilderMode && tpl.leagueKey) {
      this.form.leagueKey = sanitizeLeagueKey(tpl.leagueKey);
    }
    if (!this.fixturesBuilderMode && payload.leagueName) {
      this.form.leagueName = String(payload.leagueName).trim();
    }
    if (payload.season || tpl.season) {
      this.form.season = String(payload.season || tpl.season);
    }
    if (payload.leagueRulesId) {
      this.form.leagueRulesId = String(payload.leagueRulesId);
    }
    this.form.startDate = String(payload.startDate || this.form.startDate);
    this.form.startTime = String(payload.startTime || this.form.startTime);
    this.form.frequency = payload.frequency === 'twice' ? 'twice' : 'weekly';
    this.form.weekday1 = String(payload.weekday1 || this.form.weekday1);
    this.form.weekday2 = String(payload.weekday2 || this.form.weekday2);
    this.form.roundRobinCycles = Math.max(1, Number(payload.roundRobinCycles) || 1);
    this.form.teamMode = payload.teamMode === 'manual' ? 'manual' : 'auto';
    this.form.balanceHomeAway = payload.balanceHomeAway !== false;
    this.form.avoidVenueClashes = payload.avoidVenueClashes !== false;
    this.selectedTeamIds = Array.isArray(payload.selectedTeamIds) ? payload.selectedTeamIds.map(String) : [];
    this.customRows = Array.isArray(payload.customRows)
      ? payload.customRows.map((row) => ({ id: nextId('custom'), ...row }))
      : [];
    if (!opts.fromImport) {
      this.form.templateName = String(tpl.name || '');
    }
    this.form.templateSeasonTarget = this.form.season;

    this.selectedRule = this.leagueRulesOptions.find((r) => String(r._id) === String(this.form.leagueRulesId)) || null;
    if (this.selectedRule) this.applyScheduleFromSelectedRule();
    await this.loadTeams();
    this.rebuildPlanRows();
  },

  async saveTemplate() {
    const name = String(this.form.templateName || '').trim();
    if (!name) {
      this.$store.toastr.warning('Enter template name');
      return;
    }
    this.templateSaving = true;
    try {
      const existing = this.currentTemplate();
      const payload = {
        _id: existing?._id || undefined,
        name,
        leagueKey: this.form.leagueKey,
        season: this.form.season,
        payload: this.templatePayload(),
      };
      const res = await apiFetch('/league-builder-templates', { method: 'POST', body: payload });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) throw new Error(data?.message || 'Failed to save template');
      await this.loadTemplates();
      const savedId = data?.item?._id;
      if (savedId) this.templateSelectedId = String(savedId);
      this.$store.toastr.success(`Template "${name}" saved`);
    } catch (e) {
      this.$store.toastr.error(e.message || 'Failed to save template');
    } finally {
      this.templateSaving = false;
    }
  },

  async deleteTemplate() {
    const tpl = this.currentTemplate();
    if (!tpl) return;
    if (tpl.builtin) {
      this.$store.toastr.info('Built-in templates cannot be deleted');
      return;
    }
    if (!globalThis.confirm(`Delete template "${tpl.name}"?`)) return;
    this.templateDeleting = true;
    try {
      const res = await apiFetch(`/league-builder-templates/${encodeURIComponent(String(tpl._id))}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) throw new Error(data?.message || 'Failed to delete template');
      await this.loadTemplates();
      this.templateSelectedId = '';
      this.$store.toastr.success('Template deleted');
    } catch (e) {
      this.$store.toastr.error(e.message || 'Failed to delete template');
    } finally {
      this.templateDeleting = false;
    }
  },

  setStep(s) {
    const n = Number(s);
    if (!Number.isFinite(n) || n < 1 || n > this.totalSteps) return;
    if (n >= 2 && !this.validateBasicsStep()) {
      this.step = 1;
      return;
    }
    if (n >= 3 && this.selectedTeamIds.length < 2) {
      this.error = '';
      this.$store.toastr.warning('Select at least two teams before schedule or review.');
      this.step = 2;
      return;
    }
    if (n === 2) {
      this.loadTeams();
    }
    this.step = n;
  },

  nextStep() {
    this.setStep(this.step + 1);
  },

  prevStep() {
    if (this.step > 1) this.step -= 1;
  },

  async importDemoTeams() {
    this.error = '';
    try {
      const res = await apiFetch('/teams/demo-import', {
        method: 'POST',
        body: { league: this.form.leagueKey, season: this.form.season },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || data.error || 'Import failed');
      await this.loadTeams();
      this.$store.toastr.success(String(data.message || 'Demo teams ready'));
    } catch (e) {
      this.error = e.message || 'Import failed';
      this.$store.toastr.error(this.error);
    }
  },

  selectedTeams() {
    const set = new Set(this.selectedTeamIds.map(String));
    return this.teams.filter((t) => set.has(String(t._id)));
  },

  toggleTeam(teamId) {
    this.form.teamMode = 'manual';
    const id = String(teamId);
    if (this.selectedTeamIds.includes(id)) {
      this.selectedTeamIds = this.selectedTeamIds.filter((x) => x !== id);
    } else {
      this.selectedTeamIds = [...this.selectedTeamIds, id];
    }
  },

  enableAutoSelectTeams() {
    this.form.teamMode = 'auto';
    this.selectedTeamIds = this.teams.map((t) => String(t._id));
  },

  addCustomRow(type = 'event') {
    const baseDate = this.generatedRows[0]?.date || this.form.startDate;
    let title = 'League event';
    if (type === 'holiday') title = 'Holiday';
    if (type === 'break') title = 'League break';
    this.customRows.push({
      id: nextId('custom'),
      type,
      date: baseDate,
      title,
      venueId: '',
      homeTeamId: '',
      awayTeamId: '',
    });
    this.rebuildPlanRows();
  },

  removeCustomRow(id) {
    this.customRows = this.customRows.filter((r) => r.id !== id);
    this.rebuildPlanRows();
  },

  teamName(teamId) {
    return this.teams.find((t) => String(t._id) === String(teamId))?.name || 'Unknown';
  },

  venueName(venueId) {
    return this.venues.find((v) => String(v._id) === String(venueId))?.name || '';
  },

  teamVenueId(teamId) {
    const team = this.teams.find((t) => String(t._id) === String(teamId));
    return String(team?.venueId?._id || team?.venueId || '');
  },

  buildDateSlots(count) {
    const dates = [];
    const allowed = this.form.frequency === 'twice'
      ? [weekdayIdFromKey(this.form.weekday1), weekdayIdFromKey(this.form.weekday2)]
      : [weekdayIdFromKey(this.form.weekday1)];

    const start = new Date(`${this.form.startDate}T00:00:00`);
    const cursor = new Date(start);
    let guard = 0;
    while (dates.length < count && guard < 2500) {
      if (allowed.includes(cursor.getDay())) {
        dates.push(dateIsoOnly(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
    return dates;
  },

  allowedWeekdays() {
    if (this.form.frequency === 'twice') {
      return new Set([weekdayIdFromKey(this.form.weekday1), weekdayIdFromKey(this.form.weekday2)]);
    }
    return new Set([weekdayIdFromKey(this.form.weekday1)]);
  },

  ensureTeamCounter(counters, teamId) {
    if (!counters[teamId]) counters[teamId] = { home: 0, away: 0 };
    return counters[teamId];
  },

  balancePenalty(homeId, awayId, counters) {
    const home = this.ensureTeamCounter(counters, homeId);
    const away = this.ensureTeamCounter(counters, awayId);
    const hDiff = Math.abs((home.home + 1) - home.away);
    const aDiff = Math.abs(away.home - (away.away + 1));
    return hDiff + aDiff;
  },

  nextAllowedDate(baseDate, allowedWeekdays, usedVenueByDate, venueId) {
    let d = parseDateOnly(baseDate);
    let guard = 0;
    while (guard < 3650) {
      const day = d.getDay();
      const ymd = toDateOnly(d);
      const venueUsed = venueId ? usedVenueByDate.get(ymd)?.has(String(venueId)) : false;
      if (allowedWeekdays.has(day) && !venueUsed) return ymd;
      d.setDate(d.getDate() + 1);
      guard += 1;
    }
    return baseDate;
  },

  placeFixtureCandidate(pair, baseDate, counters, usedVenueByDate, allowedWeekdays) {
    const [a, b] = pair;
    const candidates = [
      { homeId: String(a), awayId: String(b) },
      { homeId: String(b), awayId: String(a) },
    ];

    const ranked = candidates.map((candidate) => {
      const venueId = this.teamVenueId(candidate.homeId);
      const date = this.form.avoidVenueClashes
        ? this.nextAllowedDate(baseDate, allowedWeekdays, usedVenueByDate, venueId)
        : baseDate;
      return {
        ...candidate,
        venueId,
        date,
        shiftDays: Math.max(0, Math.floor((parseDateOnly(date).getTime() - parseDateOnly(baseDate).getTime()) / 86400000)),
        penalty: this.form.balanceHomeAway ? this.balancePenalty(candidate.homeId, candidate.awayId, counters) : 0,
      };
    });

    ranked.sort((x, y) => x.shiftDays - y.shiftDays || x.penalty - y.penalty);
    return ranked[0];
  },

  applyFixtureToCounters(counters, homeId, awayId) {
    const h = this.ensureTeamCounter(counters, homeId);
    const a = this.ensureTeamCounter(counters, awayId);
    h.home += 1;
    a.away += 1;
  },

  teamBalanceRows(counters) {
    const rows = this.selectedTeams().map((team) => {
      const c = counters[String(team._id)] || { home: 0, away: 0 };
      return {
        teamId: String(team._id),
        teamName: team.name,
        home: c.home,
        away: c.away,
        diff: c.home - c.away,
      };
    });
    rows.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff) || a.teamName.localeCompare(b.teamName));
    return rows;
  },

  async generatePlan() {
    this.generating = true;
    this.error = '';
    try {
      if (this.form.teamMode === 'auto') {
        this.selectedTeamIds = this.teams.map((t) => String(t._id));
      }
      if (this.selectedTeamIds.length < 2) {
        throw new Error('Select at least 2 teams to generate fixtures.');
      }
      if (!this.form.leagueRulesId) {
        throw new Error('Select league rules before generating.');
      }
      if (!this.leagueNameTrimmed()) {
        throw new Error('Enter a league name before generating.');
      }
      const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
      if (!leagueKey || leagueKey.length < 2) {
        throw new Error('Enter a unique league ID (2–6 uppercase letters) before generating.');
      }
      this.form.leagueKey = leagueKey;

      const cycles = Math.max(1, Number(this.form.roundRobinCycles) || 1);
      const firstRound = rotateRoundRobin(this.selectedTeamIds);
      const rounds = [];
      for (let c = 0; c < cycles; c += 1) {
        for (const matches of firstRound) {
          const entries = c % 2 === 0 ? matches : matches.map(([h, a]) => [a, h]);
          rounds.push(entries);
        }
      }

      const slots = this.buildDateSlots(rounds.length);
      const allowedWeekdays = this.allowedWeekdays();
      const counters = {};
      const usedVenueByDate = new Map();

      // Reserve venues from existing custom rows first.
      for (const row of this.customRows) {
        if (!row?.date) continue;
        upsertUsedVenue(usedVenueByDate, String(row.date), String(row.venueId || ''));
      }

      const generated = rounds.flatMap((matches, roundIndex) => {
        const date = slots[roundIndex] || this.form.startDate;
        return matches.map(([homeId, awayId], i) => {
          const placed = this.placeFixtureCandidate(
            [String(homeId), String(awayId)],
            date,
            counters,
            usedVenueByDate,
            allowedWeekdays
          );
          this.applyFixtureToCounters(counters, placed.homeId, placed.awayId);
          upsertUsedVenue(usedVenueByDate, placed.date, placed.venueId);

          return {
            id: nextId('fx'),
            type: 'fixture',
            date: placed.date,
            time: this.form.startTime,
            title: '',
            roundLabel: `${this.form.leagueKey} R${roundIndex + 1}.${i + 1}`,
            homeTeamId: placed.homeId,
            awayTeamId: placed.awayId,
            venueId: placed.venueId,
            season: this.form.season,
            league: this.form.leagueKey,
          };
        });
      });

      this.generatedRows = generated;
      this.rebuildPlanRows();
      this.balanceRows = this.teamBalanceRows(counters);
      this.$store.toastr.success(`Generated ${generated.length} fixtures`);
    } catch (e) {
      this.error = e.message || 'Failed to generate plan';
      this.$store.toastr.error(this.error);
    } finally {
      this.generating = false;
    }
  },

  rebuildPlanRows() {
    const generated = this.generatedRows.map((r) => ({ ...r, origin: 'generated' }));
    const custom = this.customRows.map((r) => ({ ...r, origin: 'custom' }));
    this.planRows = [...generated, ...custom].sort((a, b) => {
      const ad = combineDateTimeToIso(a.date, a.time || this.form.startTime);
      const bd = combineDateTimeToIso(b.date, b.time || this.form.startTime);
      return ad.localeCompare(bd);
    });
    this.planWarnings = this.detectPlanWarnings(this.planRows);
  },

  detectPlanWarnings(rows) {
    const warnings = [];
    const venueDateKey = new Map();
    for (const row of rows) {
      if (!row?.venueId || !row?.date) continue;
      const key = `${row.date}::${row.venueId}`;
      const list = venueDateKey.get(key) || [];
      list.push(row);
      venueDateKey.set(key, list);
    }
    for (const [key, list] of venueDateKey.entries()) {
      if (list.length <= 1) continue;
      const [date, venueId] = key.split('::');
      warnings.push({
        kind: 'venue-clash',
        text: `${date}: ${list.length} rows share venue ${this.venueName(venueId) || venueId}`,
      });
    }
    return warnings;
  },

  planWeeks() {
    const byWeek = new Map();
    for (const row of this.planRows) {
      const key = mondayKey(row.date || this.form.startDate);
      const bucket = byWeek.get(key) || [];
      bucket.push(row);
      byWeek.set(key, bucket);
    }
    const weeks = [...byWeek.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([weekStart, rows]) => ({
        weekStart,
        weekLabel: `${dateLabelFromIso(weekStart)} week`,
        rows: rows.sort((a, b) => combineDateTimeToIso(a.date, a.time || this.form.startTime).localeCompare(combineDateTimeToIso(b.date, b.time || this.form.startTime))),
      }));
    return weeks;
  },

  rowDateTag(row) {
    const d = String(row.date || '');
    return `${weekdayLabelFromIso(d)} ${dateLabelFromIso(d)}`;
  },

  buildCreatePayload(row) {
    const rowType = String(row.type || 'fixture');
    const mappedType = rowType === 'holiday' ? 'break' : rowType;
    const isFixture = mappedType === 'fixture';
    const title = isFixture ? null : String(row.title || '').trim();
    const payload = {
      type: mappedType,
      title,
      roundLabel: isFixture ? String(row.roundLabel || '').trim() : String(row.title || '').trim(),
      league: this.form.leagueKey,
      season: this.form.season,
      startAt: combineDateTimeToIso(row.date, row.time || this.form.startTime),
      homeTeamId: isFixture ? (row.homeTeamId || null) : null,
      awayTeamId: isFixture ? (row.awayTeamId || null) : null,
      homeTeamName: isFixture ? this.teamName(row.homeTeamId) : null,
      awayTeamName: isFixture ? this.teamName(row.awayTeamId) : null,
      venueId: row.venueId || null,
      status: 'scheduled',
    };
    return { payload, isFixture };
  },

  ensureRowIsValid(row, payload, isFixture) {
    if (isFixture && (!payload.homeTeamId || !payload.awayTeamId)) {
      throw new Error(`Fixture row ${row.roundLabel || row.id} is missing teams.`);
    }
  },

  async createPlanRow(row) {
    const { payload, isFixture } = this.buildCreatePayload(row);
    this.ensureRowIsValid(row, payload, isFixture);
    const res = await apiFetch('/fixtures', { method: 'POST', body: payload });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) {
      throw new Error(data?.message || `Failed to create row ${row.roundLabel || row.title || row.id}`);
    }
  },

  async savePlan() {
    if (!this.planRows.length) {
      this.$store.toastr.warning('Generate a plan first');
      return;
    }
    if (!this.validateBasicsStep()) {
      this.step = 1;
      return;
    }
    if (!this.form.leagueRulesId) {
      this.$store.toastr.warning('Select a league rules profile before creating fixtures');
      this.step = 1;
      return;
    }
    const leagueKey = sanitizeLeagueKey(this.form.leagueKey);
    if (!leagueKey || leagueKey.length < 2) {
      this.$store.toastr.warning('Enter a league ID (2–6 uppercase letters A–Z)');
      this.step = 1;
      return;
    }
    await this.loadUsedLeagueKeys();
    if (!this.fixturesBuilderMode && this.leagueKeyInUse(leagueKey)) {
      this.$store.toastr.error(`League ID ${leagueKey} is already in use — choose another or generate one.`);
      this.step = 1;
      return;
    }
    this.form.leagueKey = leagueKey;
    this.saving = true;
    this.error = '';
    let created = 0;
    try {
      await this.saveLeagueProfile();
      await this.assignSelectedTeamsToLeague();
      for (const row of this.planRows) {
        await this.createPlanRow(row);
        created += 1;
      }

      this.$store.toastr.success(this.fixturesBuilderMode
        ? `Fixtures created: ${created} rows added.`
        : `League build complete: ${created} rows created.`);
      const dest = this.fixturesBuilderMode
        ? withLeagueId('/admin/fixtures', leagueKey)
        : `/admin/l/${encodeURIComponent(leagueKey)}`;
      const router = globalThis.PineconeRouter;
      if (router?.navigate) router.navigate(dest);
      else globalThis.location.href = dest;
    } catch (e) {
      this.error = e.message || 'Failed to create fixtures';
      this.$store.toastr.error(this.error);
    } finally {
      this.saving = false;
    }
  },

  createPlanButtonLabel() {
    return this.fixturesBuilderMode ? 'Create fixtures' : 'Create League Fixtures';
  },

  balanceRows: [],
}));
